import {findNodes} from '../../../lib/utility/dom-helpers';
import {CSS_CLASS_NAMES as CSS, MODE_NAMES, modeInfo, destinationInfo, needsDimensions} from '../settings/constants';
import {translate} from 'shared/services/I18nService';

/**
 * Provides two way binding between a user interface and a settings object. Changes to either are reflected in the other.
 * Note this isn't a pattern we want to propagate throughout this code. This is a stopgap till we include an MV* framework.
 */
export class DataBinder {
  constructor (settings) {
    this.settings = settings;
  }

  bind (container, tabs) {
    const settings = this.settings;
    const modeList = findNodes(container, 'select.snapshot-mode-options')[0];
    const modeLabel = findNodes(container, 'span.mode-name')[0];
    const sizeList = findNodes(container, `.${CSS.SIZE_OPTIONS} input`);
    const sizeListContainer = findNodes(container, '.mode-settings-container');
    const destinationList = findNodes(container, `.${CSS.DESTINATION_BUTTONS} input`);
    const messageContainer = container.querySelectorAll('.snapshot-settings-message')[0];
    let errorMessage;

    function syncSettingsToUi () {
      const shouldSetDimensions = needsDimensions[settings.mode];
      bindSelectBox(modeList, settings.mode);
      bindCheckboxes(destinationList, settings.destination);
      modeLabel.innerText = translate(`${MODE_NAMES[settings.mode]}-caption`);

      sizeList.forEach((n) => {
        n.value = settings[n.name];
        n.disabled = !shouldSetDimensions;
      });

      if (shouldSetDimensions) {
        removeClass(sizeListContainer, CSS.DISABLED);
      } else {
        addClass(sizeListContainer, CSS.DISABLED);
      }

      syncMessageText();
    }

    function syncMessageText () {
      let message = errorMessage;

      if (tabs.selectedIndex === 1) {
        message = translate(destinationInfo[settings.destination]);
      } else if (!message) {
        message = translate(modeInfo[settings.mode]).replace('$WIDTH', settings.width).replace('$HEIGHT', settings.height);
      }
      messageContainer.innerText = message;
    }

    function bindUiToSettings () {
      bindNode(modeList);
      bindList(destinationList);

      sizeList.forEach((n) => {
        n.addEventListener('change', (e) => {
          if (!n.value) {
            n.value = settings[n.name];
          } else {
            tryToSet(n, n.name, n.value);
          }
        });
      });

      syncMessageText();
    }

    function bindList (list) {
      list.forEach(bindNode);
    }
    function bindNode (node) {
      node.addEventListener('change', (e) => {
        tryToSet(node, node.name, e.target.value);
      });
    }

    function tryToSet (el, key, value) {
      try {
        settings[key] = value;
        errorMessage = '';
        removeClass(messageContainer, CSS.VALIDATION_ERROR);
        removeClass(el, CSS.VALIDATION_ERROR);
      } catch (err) {
        errorMessage = err.message;
        addClass(messageContainer, CSS.VALIDATION_ERROR);
        addClass(el, CSS.VALIDATION_ERROR);
      }
      syncMessageText();
    }

    syncSettingsToUi();
    bindUiToSettings();
    tabs.onChange = syncMessageText;

    // When settings change, resync the UI.
    this.settings.events.on('settings-service:change', syncSettingsToUi);
  }
}

// IE doesn't like `element.classList` so we have to work around it
function addClass (el, className) {
  if (Array.isArray(el)) {
    el.forEach(e => addClass(e, className));
  }
  const cName = el.className || '';
  const classes = cName.split(' ');
  if (classes.indexOf(className) === -1) {
    classes.push(className);
    el.className = classes.join(' ').trim();
  }
}
function removeClass (el, className) {
  if (Array.isArray(el)) {
    el.forEach(e => removeClass(e, className));
  }
  const cName = el.className || '';
  const classes = cName.split(' ');
  const at = classes.indexOf(className);
  if (at > -1) {
    classes.splice(at, 1);
    el.className = classes.join(' ');
  }
}
function bindCheckboxes (list, selected) {
  list.forEach(n => {
    if (n.value === selected) {
      // Note that n.setAttribute('checked', true); doesn't work reliably in IE tests. It seems to work fine in-app though.
      n.checked = true;
    }
  });
}
function bindSelectBox (list, selected) {
  findNodes(list, 'option').forEach(n => {
    // Note that n.setAttribute('checked', true); doesn't work reliably in IE tests. It seems to work fine in-app though.
    n.selected = (n.value === selected);
  });
}



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/snapshot/tool/DataBinder.js